/**
 * API and content fetching utilities
 */

import { getApiConfig } from './env'

export type NavigationLinkType = "URL" | "Reference" | "Divider"

export interface NavigationLink {
  name: string
  type?: NavigationLinkType
  url: string
}

export interface NavigationSection {
  sys: {
    id: string
  }
  name: string
  mobileName?: string
  url?: string
  isDefault?: boolean
  promotionTitle?: string
  promotionSubtitle?: string
  promotionProductProductVariant?: string
  promoProduct?: { product: any }
  promoProductCollection?: any
  linksCollection: {
    items: Array<NavigationLink | NavigationEvent>
  }
}

export interface NavigationEvent {
  sys: {
    id: string
  }
  startDate: string
  endDate?: string
  title: string
  url?: string
  type?: string
}

/**
 * Fetch header navigation items from the API
 * Returns a list of navigation items with optional children
 */
export interface HeaderNavigationLink {
  label: string
  href: string
  meta?: string
  divider?: boolean
}

export interface HeaderNavigationItem {
  label: string
  href?: string
  mobileLabel?: string
  children?: HeaderNavigationLink[]
  promotionTitle?: string
  promotionSubtitle?: string
  promotionLabel?: string
  promotionHref?: string
}

const getApiBaseUrl = () => {
  const { baseUrl } = getApiConfig()
  return (baseUrl || 'https://apis.ligonier.org/v1').replace(/\/+$/, '')
}

const getDefaultHeaderNavigationEndpoint = () => {
  return `${getApiBaseUrl()}/header`
}

const getDefaultHeaderEventsEndpoint = () => {
  return `${getApiBaseUrl()}/events`
}

const isFridayEastern = () => {
  let weekday: string

  try {
    weekday = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      timeZone: 'America/New_York',
    }).format(new Date())
  } catch {
    weekday = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
    }).format(new Date())
  }

  return weekday === 'Friday'
}

const formatHeaderEventDate = (date: string) => {
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/New_York',
    }).format(d).toUpperCase()
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(d).toUpperCase()
  }
}

// Sortable timestamp for an event start date. Unparseable dates resolve to
// Infinity so they sort to the end (rather than producing a NaN comparator,
// which yields unstable ordering and could pull invalid dates into the top 5).
const eventStartTimestamp = (startDate: string): number => {
  const time = new Date(startDate).getTime()
  return isNaN(time) ? Infinity : time
}

const fetchHeaderEvents = async (
  endpoint: string,
  abortSignal?: AbortSignal,
): Promise<Array<NavigationEvent>> => {
  const response = await fetch(endpoint, {
    method: 'GET',
    signal: abortSignal,
  })

  if (!response.ok) {
    const statusText = response.statusText || 'Unknown Error'
    throw new Error(
      `Failed to fetch header events (${response.status} ${statusText}) from ${endpoint}`,
    )
  }

  const data = await response.json()
  return Array.isArray(data) ? data : []
}

/**
 * Sanitize an untrusted href without rewriting where it points.
 * Blocks dangerous schemes and protocol-relative URLs; relative paths pass
 * through unchanged so the browser resolves them against the CURRENT origin.
 * Use this for links that live on the embedding site (e.g. breadcrumbs).
 */
export const sanitizeHref = (href?: unknown): string => {
  if (typeof href !== 'string' || !href) {
    return '#'
  }

  // Strip control characters and all whitespace to prevent bypass via embedded newlines, tabs, etc.
  // (e.g., java\nscript: normalizes to javascript: in browsers)
  const normalized = href.replace(/[\s\x00-\x1f\x7f]/g, '')

  if (!normalized || normalized.startsWith('//')) {
    return '#'
  }

  const schemeMatch = normalized.match(/^([a-zA-Z][a-zA-Z\d+.-]*):/)
  if (!schemeMatch) {
    return normalized
  }

  const scheme = schemeMatch[1]?.toLowerCase()
  return scheme === 'http' || scheme === 'https' || scheme === 'mailto' || scheme === 'tel'
    ? normalized
    : '#'
}

/**
 * Sanitize an href for HEADER/FOOTER NAVIGATION data, which is authored
 * against ligonier.org: root-relative paths are additionally rebased onto the
 * configured ligonier.org base URL so nav links resolve correctly on any host.
 * Do NOT use for site-local links (breadcrumbs) — use sanitizeHref instead.
 */
export const sanitizeHeaderHref = (href?: unknown): string => {
  const sanitized = sanitizeHref(href)

  // Root-relative path — prefix with the configured ligonier.org base URL so
  // nav links resolve correctly on any host (not just www.ligonier.org).
  if (sanitized.startsWith('/') && !sanitized.startsWith('//')) {
    const rawBase = getApiConfig().ligonierOrgUrl || 'https://www.ligonier.org'
    let base: string
    try {
      const parsed = new URL(rawBase.trim())
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        throw new Error('Invalid scheme')
      }
      base = parsed.origin
    } catch {
      base = 'https://www.ligonier.org'
    }
    return `${base}${sanitized}`
  }

  return sanitized
}

const sanitizeOptionalHeaderHref = (href?: string): string | undefined => {
  if (!href) {
    return undefined
  }

  const sanitized = sanitizeHeaderHref(href)
  return sanitized === '#' ? undefined : sanitized
}

const normalizeHeaderNavigationLink = (
  item: NavigationLink | NavigationEvent | HeaderNavigationLink,
): HeaderNavigationLink | null => {
  if (!item || typeof item !== 'object') {
    return null
  }

  if ("label" in item && "href" in item) {
    const isDivider = "divider" in item && item.divider === true
    const normalizedLabel = typeof item.label === 'string' ? item.label.trim() : ''

    if (!isDivider && normalizedLabel === '') {
      return null
    }

    const normalizedLink: HeaderNavigationLink = {
      label: isDivider ? '' : normalizedLabel,
      href: sanitizeHeaderHref(item.href),
      ...(isDivider ? { divider: true } : {}),
    }

    if (typeof item.meta === 'string' && item.meta.trim() !== '') {
      normalizedLink.meta = item.meta
    }

    return normalizedLink
  }

  if ("type" in item && item.type === "Divider") {
    return {
      label: '',
      href: '#',
      divider: true,
    }
  }

  if ("name" in item) {
    if (typeof item.name !== 'string' || item.name.trim() === '') {
      return null
    }

    // "DO NOT REMOVE" is a Contentful placeholder entry used to preserve
    // collection structure; it should never appear in the rendered nav.
    if (item.name === "DO NOT REMOVE") {
      return null
    }

    return {
      label: item.name,
      href: sanitizeHeaderHref(item.url),
    }
  }

  if (typeof item.title !== 'string' || item.title.trim() === '') {
    return null
  }

  const formattedMeta =
    typeof item.startDate === 'string' ? formatHeaderEventDate(item.startDate) : ''

  return {
    label: item.title,
    href: sanitizeHeaderHref(item.url),
    ...(formattedMeta ? { meta: formattedMeta } : {}),
  }
}

const normalizeHeaderNavigationItem = (
  item: NavigationSection | HeaderNavigationItem,
): HeaderNavigationItem => {
  if ("label" in item) {
    if (typeof item.label !== 'string' || item.label.trim() === '') {
      throw new Error('Malformed header navigation item: label must be a non-empty string.')
    }

    const children = Array.isArray(item.children)
      ? item.children.reduce<Array<HeaderNavigationLink>>((acc, link) => {
        const normalized = normalizeHeaderNavigationLink(link)
        if (normalized) {
          acc.push(normalized)
        }
        return acc
      }, [])
      : undefined

    return {
      ...item,
      label: item.label.trim(),
      href: sanitizeOptionalHeaderHref(item.href),
      children,
      promotionHref: sanitizeOptionalHeaderHref(item.promotionHref),
    }
  }

  if (typeof item.name !== 'string' || item.name.trim() === '') {
    throw new Error('Malformed header navigation section: name must be a non-empty string.')
  }

  const hideFridayPromo = !isFridayEastern()

  const children =
    item.linksCollection?.items?.reduce<Array<HeaderNavigationLink>>((acc, link) => {
      const normalized = normalizeHeaderNavigationLink(link)

      if (!normalized) {
        return acc
      }

      if (hideFridayPromo && normalized.href.includes('/collection/5-friday')) {
        return acc
      }

      if (normalized.divider && acc.length > 0) {
        acc.push(normalized)
        return acc
      }

      if (!normalized.divider) {
        acc.push(normalized)
      }

      return acc
    }, []) || []

  const promotionHandle = item.promoProduct?.product?.handle
  const collectionHandle = item.promoProductCollection?.handle

  return {
    label: item.name.trim(),
    href: sanitizeOptionalHeaderHref(item.url),
    mobileLabel: item.mobileName,
    children,
    promotionTitle: item.promotionTitle || undefined,
    promotionSubtitle: item.promotionSubtitle || undefined,
    promotionLabel: item.promoProductCollection?.title || undefined,
    promotionHref: promotionHandle
      ? `https://store.ligonier.org/${promotionHandle}`
      : collectionHandle
        ? `https://store.ligonier.org/collection/${collectionHandle}`
        : undefined,
  }
}

const enrichHeaderNavigationItems = (
  items: HeaderNavigationItem[],
  events: Array<NavigationEvent>,
) => {
  return items.map((item) => {
    if (typeof item.label !== 'string' || item.label.trim() === '') {
      return item
    }

    if (!item.label.toLowerCase().includes('events')) {
      return item
    }

    const validEvents = events
      .filter((event): event is NavigationEvent => {
        if (!event || typeof event !== 'object') {
          return false
        }

        return (
          typeof event.title === 'string' &&
          event.title.trim() !== '' &&
          typeof event.startDate === 'string' &&
          event.startDate.trim() !== ''
        )
      })
      .sort((a, b) => eventStartTimestamp(a.startDate) - eventStartTimestamp(b.startDate))
      .slice(0, 5)

    const enrichedChildren = validEvents.reduce<Array<HeaderNavigationLink>>((acc, event) => {
      const href = sanitizeOptionalHeaderHref(event.url || item.href)
      if (href && typeof event.startDate === 'string' && event.startDate.trim() !== '') {
        acc.push({
          label: event.title,
          href,
          meta: formatHeaderEventDate(event.startDate),
        })
      }
      return acc
    }, [])

    if (enrichedChildren.length === 0) {
      return item
    }

    return {
      ...item,
      children: enrichedChildren,
    }
  })
}

export const fetchHeaderNavigation = async (
  abortSignal?: AbortSignal
): Promise<HeaderNavigationItem[]> => {
  const resolvedEndpoint = getDefaultHeaderNavigationEndpoint()
  const response = await fetch(resolvedEndpoint, {
    method: 'GET',
    signal: abortSignal,
  })

  if (!response.ok) {
    const statusText = response.statusText || 'Unknown Error'
    throw new Error(
      `Failed to fetch header navigation (${response.status} ${statusText}) from ${resolvedEndpoint}`,
    )
  }

  const data = await response.json()
  const items: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : []

  let droppedItemCount = 0
  const normalizedItems = items.reduce((acc: HeaderNavigationItem[], item: unknown) => {
    if (!item || typeof item !== 'object') {
      droppedItemCount += 1
      return acc
    }

    try {
      acc.push(
        normalizeHeaderNavigationItem(item as NavigationSection | HeaderNavigationItem),
      )
    } catch {
      // Skip malformed records so one bad item doesn't fail the whole nav.
      droppedItemCount += 1
    }

    return acc
  }, [])

  if (droppedItemCount > 0) {
    console.warn('Dropped malformed header navigation items during normalization.', {
      endpoint: resolvedEndpoint,
      droppedItemCount,
      receivedItemCount: items.length,
    })
  }

  const hasEventsSection = normalizedItems.some((item) =>
    typeof item.label === 'string' && item.label.toLowerCase().includes('events'),
  )

  if (!hasEventsSection) {
    return normalizedItems
  }

  try {
    const events = await fetchHeaderEvents(
      getDefaultHeaderEventsEndpoint(),
      abortSignal,
    )

    return enrichHeaderNavigationItems(normalizedItems, events)
  } catch (eventsError) {
    const isAbortError =
      abortSignal?.aborted ||
      (eventsError instanceof DOMException && eventsError.name === 'AbortError') ||
      (eventsError instanceof Error && eventsError.name === 'AbortError')

    if (isAbortError) {
      throw eventsError
    }

    console.warn('Failed to fetch header events for enrichment:', eventsError)
    return normalizedItems
  }
}

const getDefaultFooterNavigationEndpoint = () => {
  return `${getApiBaseUrl()}/footer`
}

export const fetchFooterNavigation = async (
  abortSignal?: AbortSignal,
): Promise<NavigationSection[]> => {
  const resolvedEndpoint = getDefaultFooterNavigationEndpoint()
  const response = await fetch(resolvedEndpoint, {
    method: 'GET',
    signal: abortSignal,
  })

  if (!response.ok) {
    const statusText = response.statusText || 'Unknown Error'
    throw new Error(
      `Failed to fetch footer navigation (${response.status} ${statusText}) from ${resolvedEndpoint}`,
    )
  }

  const data = await response.json()
  const items: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : []

  let droppedItemCount = 0
  const sections = items.reduce((acc: NavigationSection[], item: unknown) => {
    if (
      !item ||
      typeof item !== 'object' ||
      typeof (item as NavigationSection).name !== 'string' ||
      (item as NavigationSection).name.trim() === '' ||
      typeof (item as NavigationSection).sys?.id !== 'string' ||
      !Array.isArray((item as NavigationSection).linksCollection?.items)
    ) {
      droppedItemCount += 1
      return acc
    }

    const section = item as NavigationSection
    const filteredItems = (section.linksCollection?.items ?? []).filter(
      (link) => link != null && typeof link === 'object' && !('name' in link && link.name === 'DO NOT REMOVE'),
    )
    acc.push({ ...section, linksCollection: { items: filteredItems } })
    return acc
  }, [])

  if (droppedItemCount > 0) {
    console.warn('Dropped malformed footer navigation sections during normalization.', {
      endpoint: resolvedEndpoint,
      droppedItemCount,
      receivedItemCount: items.length,
    })
  }

  const hasEventsSection = sections.some(
    (section) => typeof section.name === 'string' && section.name.toLowerCase().includes('events'),
  )

  if (!hasEventsSection) {
    return sections
  }

  try {
    const events = await fetchHeaderEvents(getDefaultHeaderEventsEndpoint(), abortSignal)

    const validEvents = events
      .filter(
        (event): event is NavigationEvent =>
          event != null &&
          typeof event === 'object' &&
          typeof event.sys?.id === 'string' &&
          typeof event.title === 'string' &&
          event.title.trim() !== '' &&
          typeof event.startDate === 'string' &&
          event.startDate.trim() !== '',
      )
      .sort((a, b) => eventStartTimestamp(a.startDate) - eventStartTimestamp(b.startDate))
      .slice(0, 5)

    return sections.map((section) => {
      if (
        typeof section.name !== 'string' ||
        !section.name.toLowerCase().includes('events')
      ) {
        return section
      }

      return {
        ...section,
        linksCollection: {
          items: validEvents.map((event): NavigationEvent => ({
            sys: { id: event.sys.id },
            startDate: event.startDate,
            endDate: event.endDate,
            title: event.title,
            url: event.url ?? '#',
            type: event.type,
          })),
        },
      }
    })
  } catch (eventsError) {
    const isAbortError =
      abortSignal?.aborted ||
      (eventsError instanceof DOMException && eventsError.name === 'AbortError') ||
      (eventsError instanceof Error && eventsError.name === 'AbortError')

    if (isAbortError) {
      throw eventsError
    }

    console.warn('Failed to fetch footer events for enrichment:', eventsError)
    return sections
  }
}